/**
 * CLE-2811 — the run, chat, standing-instructions and progress endpoints below exist only on the API
 * built from the same commit. Ship the SPA and the API together; a frontend deployed ahead of the API
 * calls routes that are not there yet.
 */
import { api } from ".";
import { IContractType } from "./contractFeeSchedules";

export type DraftRunStatus =
  | "collecting"
  | "queued"
  | "running"
  | "worker_returned"
  | "awaiting_input"
  | "completed"
  | "failed"
  | "dead"
  | "canceled";

export type DraftConfidence = "High" | "Medium" | "Low";

/**
 * CLE-2811 — how much a generation models. `facts`: only the contract term(s), fast, so the reviewer can
 * clean them up and save them onto the contract first. `full`: every construct. "Continue to rates"
 * moves a facts run to full.
 */
export type DraftRunStage = "facts" | "full";

export type DraftRecStatus =
  | "pending"
  | "accepted"
  | "overridden"
  | "rejected"
  | "created"
  | "needs_input"
  | "superseded";

export type DraftComponentType =
  | "provider_decomposition"
  | "payer_consolidation"
  | "contract_term"
  | "base_rate_file"
  | "rate_calculation"
  | "carveout"
  | "case_rate_ruleset"
  | "inpatient_ruleset"
  | "package_ruleset"
  | "exploding_fee_schedule"
  | "mpdr_ruleset"
  | "lesser_of_charges"
  | "charge_based_pricing"
  | "unit_cap_ruleset"
  | "ma_payer_link";

export type DraftOverrideCategory =
  | "agent_misread_source"
  | "agent_missed_document"
  | "wrong_policy_mapping"
  | "contract_ambiguous";

export type DraftDocKind =
  | "contract_pdf"
  | "rate_excel"
  | "amendment"
  | "carveout_exhibit"
  | "other";

export interface IDraftAttribution {
  id: string;
  documentId: string | null;
  locator: string | null;
  fileName: string | null;
  exactQuote: string;
  verified: boolean;
  verificationMethod: string | null;
  transform: string | null;
}

export interface IDraftOverride {
  category: DraftOverrideCategory;
  reason: string;
  finalValue: unknown | null;
  agentWasWrong: boolean;
  createdAt: string;
}

export interface IValidationError {
  path: string;
  message: string;
}

/** A base rate file a live contract already prices this provider+payer against (a possible reuse). */
export interface IExistingBaseRateFile {
  displayName: string;
  rowCount: number | null;
  effectiveDate: string | null;
}
export interface IExistingBaseRateFiles {
  files: IExistingBaseRateFile[];
}

export type DraftChangeKind =
  | "new"
  | "unchanged"
  | "modified"
  | "conflicted"
  | "superseded";

/** A field the reviewer CORRECTED that the agent has since moved to a third value. She decides. */
export interface IFieldConflict {
  path: string;
  /** What the agent proposed last version. */
  base: unknown;
  /** What she corrected it to. We hold this until she resolves the conflict. */
  ours: unknown;
  /** What the agent proposes now. */
  theirs: unknown;
}

export interface IMergeDetails {
  conflicts: IFieldConflict[];
  changedFields: string[];
}

export interface IDraftMessage {
  id: string;
  stepId: string | null;
  recommendationId: string | null;
  /** `user` = the reviewer; `assistant` = the agent's reply (CLE-2811), stamped with its version. */
  role: string;
  body: string;
  /** Null = the agent has not seen this yet. */
  appliedInVersionId: string | null;
  createdAt: string;
}

export interface IDraftRunVersion {
  id: string;
  versionNo: number;
  createdAt: string;
}

/** CLE-2811 — POST /chat: the run, and whether her message only queued (a pass is running, or none yet). */
export interface IChatResult {
  run: IDraftRunDetail;
  queuedOnly: boolean;
}

export interface IMergeSummary {
  total: number;
  unchanged: number;
  modified: number;
  conflicted: number;
  new: number;
  superseded: number;
  /** She already BUILT these and they changed or were withdrawn. The loudest state in the tool. */
  alreadyBuilt: number;
}

export interface IDraftRecommendation {
  id: string;
  componentType: DraftComponentType;
  proposedModeling: unknown;
  /** The agent's original proposal, captured the first time a human edits. Null = untouched. */
  originalModeling: unknown | null;
  /** Field-scoped schema failures. Non-empty ⇒ status is `needs_input` and the rec must be fixed. */
  validationErrors: IValidationError[] | null;
  /** How this relates to the previous version. Null on a run that has only ever been generated once. */
  changeKind: DraftChangeKind | null;
  mergeDetails: IMergeDetails | null;
  /** The agent's account of WHY this moved. Cause attribution — a change she can't explain, she can't trust. */
  changeReason: string | null;
  stableKey: string | null;
  altModeling: unknown | null;
  confidence: DraftConfidence;
  status: DraftRecStatus;
  missingInputs: unknown | null;
  openQuestions: unknown | null;
  implementedAt: string | null;
  sortOrder: number;
  attributions: IDraftAttribution[];
  override: IDraftOverride | null;
}

export interface IDraftDocument {
  id: string;
  kind: DraftDocKind;
  fileName: string;
  contentType: string | null;
  fileSize: number | null;
  extractionStatus: "pending" | "extracting" | "ready" | "failed";
}

export interface IDraftRunSummary {
  id: string;
  accountId: string;
  /**
   * CLE-2811 — the account (hospital) the run models; null when its account row is gone. A super admin's list spans
   * every account. List rows only, and optional so a list from an older API still renders.
   */
  accountName?: string | null;
  name: string | null;
  status: DraftRunStatus;
  createdById: string;
  /** CLE-2811 — who started the run (name, else email); null when the user row is gone. List rows only, optional. */
  createdByName?: string | null;
  providerId: string | null;
  providerName: string | null;
  payerId: number | null;
  payerName: string | null;
  siteTier: DraftSiteTier | null;
  /** Free-text steering the reviewer gave the model at generation time (e.g. "model Network P only"). */
  generationInstructions: string | null;
  /**
   * CLE-2811 — the existing contract this run models, when it was started from the contract page.
   * Null means applying a contract term CREATES a draft contract instead of updating one.
   */
  contractFeeScheduleId: string | null;
  /** CLE-2811 — `facts` shows only the facts steps plus "Continue to rates"; `full` is the whole wizard. */
  stage: DraftRunStage;
  /** CLE-2811 — the contract type the AI was given. Null = it decided. ASC/Professional hide Inpatient. */
  contractType: IContractType | null;
  createdAt: string;
  updatedAt: string;
  documentCount: number;
  recommendationCount: number;
}

/**
 * CLE-2811 — the run list: at most `limit` runs, newest first. `truncated` is true when the API left older runs out,
 * so the page can say its (client-side filtered) view is not every run.
 */
export interface IDraftRunList {
  runs: IDraftRunSummary[];
  truncated: boolean;
  /** How many runs the API lists at most; null from an API that does not say. */
  limit: number | null;
}

/** `GET /runs` → `IDraftRunList`. An older API sends only `data`: not truncated, no limit. */
export const toDraftRunList = (r: {
  data: IDraftRunSummary[];
  truncated?: boolean;
  limit?: number;
}): IDraftRunList => ({
  runs: r.data,
  truncated: r.truncated === true,
  limit: typeof r.limit === "number" ? r.limit : null,
});

export interface IDraftRunDetail extends IDraftRunSummary {
  latestVersionId: string | null;
  versionNo: number;
  documents: IDraftDocument[];
  recommendations: IDraftRecommendation[];
  messages: IDraftMessage[];
  /** Every version, oldest first — the conversation's "Version N" separators. */
  versions: IDraftRunVersion[];
  /** There is input the agent has not seen: a queued instruction, or a document added since the last run. */
  isStale: boolean;
  pendingInstructionCount: number;
  /** The last regeneration's diff. `unchanged / total` is the trust metric — shown, not hidden. */
  mergeSummary: IMergeSummary | null;
  /** CLE-2811 — when the worker claimed the current generation; the run page counts elapsed time from it. */
  startedAt: string | null;
  /** CLE-2811 — what the worker is doing right now. Only while `running` / `worker_returned`. */
  progress: IWorkerProgress | null;
  /** CLE-2811 — median claim-to-finish of recent runs of this stage. Only while generating; null with < 2 samples. */
  typicalDurationMs: number | null;
  /**
   * CLE-2811 — why the last pass failed, as the worker or clear-api wrote it (a budget message, "worker
   * lease expired", a validation error). Null when it did not fail. The run page humanizes it
   * (`humanizeRunError`) and keeps the raw text under "Details".
   */
  lastError: string | null;
  /**
   * CLE-2811 (facts first) — when a FIRST generation's contract facts were persisted while the pass is
   * still running. While it is set and the run is processing, the latest version already holds the real
   * `contract_term` recommendations with the facts filled and the reimbursement slots EMPTY; when the pass
   * completes the same recommendations (same ids) get their slots filled, and a term she had accepted
   * flips back to `pending` with a "Reimbursement schedule added…" `changeReason`. Null otherwise.
   */
  interimAt: string | null;
  /**
   * CLE-2811 (in place) — a REVISION pass is running: a pass (a chat message, Regenerate, Continue to rates) on
   * a run that already has a model. While it runs, `recommendations`, `versionNo` and `mergeSummary` are the
   * VERSION ON SCREEN's (the newest earlier version with recommendations), so the cards stay put, and the page
   * shows the pass's progress in the conversation instead of the first pass's strip. A chat change the facts
   * pipeline makes lands IN PLACE on those same recommendations (same ids). `latestVersionId` is still the
   * version being written. Optional: an older API sends none (read as false).
   */
  revisionInFlight?: boolean;
  /**
   * CLE-2811 (2026-09-28) — the name of every payer id this run refers to, keyed by the id as a string: the
   * run's payer, its contract's payer, every recommendation's `payerId`, and the live partial's plans and
   * terms. An id with no Payer row is absent. The page resolves every payer it shows through this first
   * (`payerNames.ts`) — Ani: "Instead of id show the name." Optional: an older API sends none.
   */
  payerNames?: Record<string, string>;
  /**
   * CLE-2811 (2026-09-28) — what the contract this run models holds right now: her notes and the facts
   * already saved, read live on every fetch. Null for a standalone run (and when its contract is gone).
   * Shown on the Contract Terms step ("Your notes on this contract") and as "In production now".
   */
  contractOnFile?: IContractOnFile | null;
}

/** CLE-2811 (2026-09-28) — a contract's stored facts as the run page shows them. Every empty value is null. */
export interface IContractOnFile {
  contractFeeScheduleId: string;
  payerId: number;
  payerName: string | null;
  contractType: IContractType | null;
  /** YYYY-MM-DD. */
  signedDate: string | null;
  /** The FEE effective date, YYYY-MM-DD. */
  effectiveDate: string | null;
  timelyFilingDays: number | null;
  implantTerms: string | null;
  multipleProcedureMethodology: string | null;
  /** Her own notes on the contract — Ani: "In AI modeling I also need to see my notes." */
  notes: string | null;
  /**
   * Each slot is capped at 20,000 characters by the server (a row-per-code schedule can run far longer);
   * `truncatedSlots` names the ones it cut.
   */
  reimbursement: {
    inpatient: string | null;
    outpatient: string | null;
    facility: string | null;
    nonFacility: string | null;
  };
  /**
   * CLE-2811 (2026-09-28) — the `reimbursement` slots the server cut at its 20,000-character cap
   * ("outpatient", …). The card shows what it received and points to the contract for the rest.
   * Optional: an older API sends none (and does not cap).
   */
  truncatedSlots?: string[];
}

/**
 * CLE-2811 — `GET /runs/:id/progress`: the slim poll the run page makes every 1.5 s while a pass is in
 * flight, instead of re-downloading the whole run (recommendations, attributions, messages) every 4 s.
 * The page refetches the full run only when `status`, `latestVersionId`, `messageCount`, `versionCount`
 * or `interimAt` moves (`shouldRefetchRun`), and every 10 s as a safety net.
 */
export interface IDraftRunProgress {
  id: string;
  status: DraftRunStatus;
  stage: DraftRunStage;
  startedAt: string | null;
  updatedAt: string;
  /**
   * The row's `enqueuedAt`, restamped by every enqueue (generate, regenerate, chat, auto-start; a retry
   * after a failure or a reaped lease keeps it). Not read by the page, which times the queue from `updatedAt`.
   */
  enqueuedAt: string | null;
  latestVersionId: string | null;
  messageCount: number;
  versionCount: number;
  /** Where a `queued` run is in the worker's queue (1 = next); null when not queued or unknown. */
  queuePosition: number | null;
  progress: IWorkerProgress | null;
  typicalDurationMs: number | null;
  /** CLE-2811 (facts first) — the run's `interimAt` (see `IDraftRunDetail`); a change refetches the run. */
  interimAt: string | null;
  /** CLE-2811 (in place) — as on `IDraftRunDetail`. Optional: an older API sends none. */
  revisionInFlight?: boolean;
  /**
   * CLE-2811 (2026-09-28) — the names of the payer ids in `progress.partial` (plans and terms found mid-pass),
   * fresher than the run's own `payerNames`, which refetches only when the pass moves. Optional.
   */
  payerNames?: Record<string, string>;
}

/**
 * CLE-2811 — `GET /contracts/:contractFeeScheduleId/latest`: the newest contract model of one contract,
 * slim, for the contract page's banner and its "Open latest model" button. Null when there is none.
 */
export interface IContractLatestModelRun {
  id: string;
  status: DraftRunStatus;
  stage: DraftRunStage;
  updatedAt: string;
  interimAt: string | null;
  recommendationCount: number;
  lastError: string | null;
  /**
   * CLE-2811 (2026-09-28) — how the model relates to this contract. `this_contract`: it models this contract.
   * `saved_here`: a model on ANOTHER contract (or a standalone one) saved a contract term into this one —
   * typically it created it. The banner says so. Optional: an older API sends neither field.
   */
  origin?: "this_contract" | "saved_here";
  /** For `saved_here`: the contract that model is on. Null for a standalone model and for `this_contract`. */
  sourceContractFeeScheduleId?: string | null;
}

/** CLE-2811 — the phases the worker reports while it holds a run. */
export type WorkerProgressPhase =
  | "downloading"
  | "extracting"
  | "thinking"
  | "depositing";

/**
 * CLE-2811 (progress v2) — where one document is in the worker's download-and-read pipeline. `reused`: the
 * previous pass's text; `preextracted` (batch two): the text extracted when the file was uploaded. Both are
 * read without reading it again.
 */
export type WorkerDocumentStatus =
  | "pending"
  | "downloading"
  | "extracting"
  | "done"
  | "reused"
  | "preextracted";

/** CLE-2811 (progress v2) — one run document as the worker reports it. */
export interface IWorkerDocumentProgress {
  documentId: string;
  fileName: string;
  /** Page count once the worker has opened it; null for a workbook or before it is opened. */
  pages: number | null;
  /** Pages read so far. */
  done: number;
  /** Scanned pages read by OCR so far. */
  ocrPages: number;
  status: WorkerDocumentStatus;
  /**
   * CLE-2811 batch two — where a `reused` document's text came from: "text extracted at upload"
   * (`UPLOAD_EXTRACTION_DETAIL`, read like `preextracted`) or "reusing text from the previous pass".
   */
  detail?: string | null;
}

/** CLE-2811 (progress v2) — how long each finished phase took, in ms. */
export interface IWorkerCompletedPhases {
  downloading?: number | null;
  extracting?: number | null;
}

/** CLE-2811 (progress v2) — a plan (line of business) the worker has found, and whether its terms are read. */
export interface IWorkerPartialPlan {
  name: string;
  payerId: number | null;
  status: "pending" | "extracting" | "done" | "failed";
}

/** CLE-2811 (progress v2) — one plan's contract terms as the worker reads them. */
export interface IWorkerPartialTerm {
  payerId: number | null;
  name: string;
  status: "extracting" | "done" | "failed";
  /** The contract term as it will be proposed — the `contract_term` shape; present once `done`. */
  proposedModeling?: Record<string, unknown> | null;
  openQuestions?: string[] | null;
}

/** CLE-2811 (progress v2) — the contract-wide facts read so far. */
export interface IWorkerPartialFacts {
  signedDate?: string | null;
  effectiveDate?: string | null;
  timelyFilingDays?: number | null;
  /** A sentence, or `{ percentPerYear?, basis?, firstIncreaseOn? }` like the contract term's. */
  escalator?: unknown;
  implantTerms?: string | null;
  multipleProcedureMethodology?: string | null;
}

/**
 * CLE-2811 (progress v2) — what the worker has read so far, before the pass finishes: the run page
 * previews it in the wizard (not saved, not editable) until the real recommendations arrive.
 */
export interface IWorkerPartial {
  contractType?: string | null;
  facts?: IWorkerPartialFacts | null;
  plans?: IWorkerPartialPlan[] | null;
  terms?: IWorkerPartialTerm[] | null;
}

/**
 * CLE-2811 — the worker's live progress (clear-api's `WorkerProgress`). `current`/`total` are "document n
 * of N" while downloading and "pages done of N" while extracting a PDF; `ocrPages` counts that document's
 * scanned pages OCR'd so far. Timestamps are ISO strings from the worker's clock.
 *
 * The fields after `updatedAt` are progress v2 and arrive with a later worker build: every one of them is
 * optional and may be null, and the run page must read well without any of them.
 */
export interface IWorkerProgress {
  phase: WorkerProgressPhase;
  /** The file being downloaded or read, or "reusing text from the previous pass". */
  detail: string | null;
  current: number | null;
  total: number | null;
  ocrPages: number | null;
  /** The model the worker gave the AI; null for the CLI default. */
  model: string | null;
  thinkingStartedAt: string | null;
  phaseStartedAt: string | null;
  updatedAt: string | null;
  /** v2 — every run document and where it is. */
  documents?: IWorkerDocumentProgress[] | null;
  /** v2 — the duration of each finished phase, in ms. */
  completedPhases?: IWorkerCompletedPhases | null;
  /** v2 — the AI's own streamed text so far: the last few thousand characters, newest at the end. */
  narration?: string | null;
  /** v2 — the AI's last few tool actions in plain words, oldest first. */
  activity?: string[] | null;
  /** v2 — how many turns the AI has taken. */
  turns?: number | null;
  /** v2 — what has been read so far (plans, their terms, the contract facts), for the wizard's preview. */
  partial?: IWorkerPartial | null;
  /**
   * CLE-2811 — present when the worker left the fast facts pipeline for the slower legacy path, with the
   * pipeline's reason. The strip says so in a muted line, since that path takes noticeably longer.
   */
  fallback?: { reason: string } | null;
}

export interface IPresignedUpload {
  documentId: string;
  uploadUrl: string;
  s3Key: string;
}

export type DraftCompareStatus = "match" | "missing" | "differs" | "extra";

export interface IDraftCompareItem {
  recommendationId: string | null;
  componentType: string | null;
  liveKey: string | null;
  status: DraftCompareStatus;
}

export interface IDraftCompareResult {
  compareId: string;
  items: IDraftCompareItem[];
}

// Per-recommendation self-verify result: how one recommendation lines up with production.
export type DraftVerdict = "match" | "differs" | "present" | "missing" | "n_a";
export interface IDraftRecMatch {
  verdict: DraftVerdict;
  rows: Record<string, unknown>[];
}
// Keyed by recommendationId.
export type IDraftVerification = Record<string, IDraftRecMatch>;

/** "This contract looks modeled under a different provider" — the sibling entities that already have it. */
export interface IProviderHint {
  otherProviders: { id: string; name: string }[];
}

export type DraftSiteTier = "non_facility" | "facility" | "both";

export interface ICreateDraftRunRequest {
  name?: string;
  providerId?: string;
  payerId?: number;
  siteTier?: DraftSiteTier;
  note?: string;
  idempotencyKey?: string;
  /**
   * CLE-2811 — model an EXISTING contract. Provider and payer are then derived from that contract
   * and any values passed alongside are ignored, so the two can never disagree. The contract's
   * attachments are seeded as the run's documents.
   */
  contractFeeScheduleId?: string;
  /** CLE-2811 — defaults to `facts` for a contract run, `full` otherwise. */
  stage?: DraftRunStage;
  /** CLE-2811 — given to the AI. Omitted: the contract's own type. `null`: "not sure, let the AI decide". */
  contractType?: IContractType | null;
}

/** CLE-2811 — one field's outcome when a contract term is written to a contract. */
export interface IApplyContractTermChange {
  field: string;
  from: string | null;
  to: string | null;
  /**
   * `skipped` means the contract already had a value and it is kept. `replaced` only happens when the
   * reviewer ticked "Replace text the contract already has", and only for the free-text facts. The
   * contract's notes are never a field here (2026-10-02): the AI's notes stay on the model.
   */
  action: "filled" | "replaced" | "skipped";
}

export interface IApplyContractTermResult {
  /** Null only on a dry run that would create a contract — there is no id until it is written. */
  contractFeeScheduleId: string | null;
  created: boolean;
  /** False for a dry run, and when the contract already had every field filled. */
  applied: boolean;
  changes: IApplyContractTermChange[];
  /** CLE-2811 — which contract the save writes to, and why. */
  target: IApplyContractTermTarget;
  /** CLE-2811 — the run's documents copied onto a NEW contract (dry run: would be). Null for an existing one. */
  documents: IApplyContractTermDocuments | null;
}

/**
 * CLE-2811 — where a save writes. `run_contract`: the contract the run models. `other_contract`: another
 * contract of the run's provider for the term's payer. `create`: a new Draft contract for the term's payer.
 */
export type ApplyContractTermTargetMode =
  | "run_contract"
  | "other_contract"
  | "create";

/** CLE-2811 — an existing contract of the run's provider for the term's payer. */
export interface IApplyContractTermCandidate {
  id: string;
  /** "SuperMed (effective 2026-03-01)" — contracts have no name of their own. */
  name: string;
  status: string;
  /** YYYY-MM-DD. */
  effectiveDate: string;
}

export interface IApplyContractTermTarget {
  mode: ApplyContractTermTargetMode;
  /** Null only for `create` on a dry run. */
  contractFeeScheduleId: string | null;
  contractName?: string;
  /** The TERM's payer — for an off-payer term, not the run's. */
  payerName: string;
  /**
   * The term is for a payer other than the run's: a save must send `targetContractFeeScheduleId` or
   * `createNewContract`, and a dry run without either is only the server's proposal.
   */
  requiresChoice: boolean;
  /** Best first: Active, Draft, then others; newest effective date first. */
  existingCandidates: IApplyContractTermCandidate[];
  /**
   * CLE-2811 (2026-09-28) — the contract this term was already saved into, while it is still a valid
   * target for it (the provider's, the term's payer). Save draft saves there again rather than asking
   * her to pick among several contracts she already picked from. Optional: an older API sends none.
   */
  alreadySavedTo?: string | null;
}

export interface IApplyContractTermDocuments {
  copied: string[];
  notCopied: string[];
}

export interface IApplyContractTermRequest {
  runId: string;
  recommendationId: string;
  dryRun?: boolean;
  /** CLE-2811 — overwrite the contract's existing free text (implant terms, reimbursement). */
  replace?: boolean;
  /** CLE-2811 — an existing contract of the run's provider for the term's payer. */
  targetContractFeeScheduleId?: string;
  /** CLE-2811 — a new Draft contract for the term's payer (idempotent per recommendation). */
  createNewContract?: boolean;
}

export interface IPresignDocumentRequest {
  runId: string;
  fileName: string;
  contentType: string;
  kind?: DraftDocKind;
  fileSize?: number;
}

export interface IOverrideRequest {
  recommendationId: string;
  category: DraftOverrideCategory;
  reason: string;
  finalValue?: unknown;
  agentWasWrong?: boolean;
}

export interface IAcceptRequest {
  recommendationId: string;
  editedModeling?: Record<string, unknown>;
}

/** CLE-2811 — one saved version of a contract type's standing instructions. */
export interface IContractModelingInstructionVersion {
  id: string;
  version: number;
  /** Empty string = the instructions were cleared in this version. */
  body: string;
  createdAt: string;
  /** Null for the seeded version 1. `name` is null when the author's user no longer exists. */
  createdBy: { id: string; name: string | null } | null;
}

/** CLE-2811 — one contract type on the "Standing instructions" tab. */
export interface IContractModelingInstructionEntry {
  contractType: IContractType;
  latest: IContractModelingInstructionVersion | null;
  /** Older versions, newest first. */
  history: IContractModelingInstructionVersion[];
}

// The standing-instructions tag is registered here rather than in the shared tagTypes list, so this
// feature's cache stays self-contained in its own query file.
const apiWithInstructionTags = api.enhanceEndpoints({
  addTagTypes: ["ContractModelingInstructions"],
});

const contractModelDraftsQueries = apiWithInstructionTags.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Canceled, failed and dead runs are hidden by the server unless `includeInactive` is true. The newest 500 at
     * most; `truncated` says older ones were left out.
     */
    listContractModelDraftRuns: builder.query<
      IDraftRunList,
      { includeInactive?: boolean } | void
    >({
      query: (arg) => ({
        url: "/contract-model-drafts/runs",
        params: arg?.includeInactive ? { includeInactive: "true" } : undefined,
      }),
      transformResponse: toDraftRunList,
      providesTags: ["ContractModelDrafts"],
    }),
    getContractModelDraftRun: builder.query<IDraftRunDetail, string>({
      query: (id) => ({ url: `/contract-model-drafts/runs/${id}` }),
      transformResponse: (r: { data: IDraftRunDetail }) => r.data,
      providesTags: ["ContractModelDrafts"],
    }),
    /**
     * CLE-2811 — the slim progress poll. Deliberately provides NO tag: a mutation's invalidation must not
     * refetch it, and a progress tick must never refetch the whole run. The run page drives both.
     */
    getContractModelDraftRunProgress: builder.query<IDraftRunProgress, string>({
      query: (id) => ({ url: `/contract-model-drafts/runs/${id}/progress` }),
      transformResponse: (r: { data: IDraftRunProgress }) => r.data,
    }),
    /**
     * CLE-2811 — the newest contract model of a contract (the contract page's banner and buttons). Tagged
     * like the run, so starting a model — or an upload that starts one — brings the banner in.
     */
    getLatestContractModelRun: builder.query<
      IContractLatestModelRun | null,
      string
    >({
      query: (contractFeeScheduleId) => ({
        url: `/contract-model-drafts/contracts/${contractFeeScheduleId}/latest`,
      }),
      transformResponse: (r: { data?: IContractLatestModelRun | null }) =>
        r?.data ?? null,
      providesTags: ["ContractModelDrafts"],
    }),
    createContractModelDraftRun: builder.mutation<
      IDraftRunDetail,
      ICreateDraftRunRequest
    >({
      query: (body) => ({
        url: "/contract-model-drafts/runs",
        method: "POST",
        body,
      }),
      transformResponse: (r: { data: IDraftRunDetail }) => r.data,
      invalidatesTags: ["ContractModelDrafts"],
    }),
    /**
     * CLE-2811 — write one accepted contract term's facts onto a real contract.
     *
     * `dryRun: true` returns the identical field plan without writing, which is what the wizard
     * previews with — so the reviewer sees exactly what would change before a production contract
     * is touched. A dry run must NOT invalidate the cache; only a real write does.
     */
    applyContractModelDraftContractTerm: builder.mutation<
      IApplyContractTermResult,
      IApplyContractTermRequest
    >({
      query: ({ runId, ...body }) => ({
        url: `/contract-model-drafts/runs/${runId}/apply-contract-term`,
        method: "POST",
        body,
      }),
      transformResponse: (r: { data: IApplyContractTermResult }) => r.data,
      invalidatesTags: (result, error, { dryRun }) =>
        dryRun ? [] : ["ContractModelDrafts", "ContractFeeSchedules"],
    }),
    presignContractModelDraftDocument: builder.mutation<
      IPresignedUpload,
      IPresignDocumentRequest
    >({
      query: ({ runId, ...body }) => ({
        url: `/contract-model-drafts/runs/${runId}/documents`,
        method: "POST",
        body,
      }),
      transformResponse: (r: { data: IPresignedUpload }) => r.data,
      // Deliberately does NOT invalidate: this only reserves a row; the bytes aren't in S3 yet.
      // confirmContractModelDraftDocument invalidates once the upload is verified, so the
      // document list never shows a file that isn't really there.
    }),
    confirmContractModelDraftDocument: builder.mutation<
      IDraftRunDetail,
      { runId: string; documentId: string }
    >({
      query: ({ runId, documentId }) => ({
        url: `/contract-model-drafts/runs/${runId}/documents/${documentId}/confirm`,
        method: "POST",
      }),
      transformResponse: (r: { data: IDraftRunDetail }) => r.data,
      invalidatesTags: ["ContractModelDrafts"],
    }),
    runContractModelDraftGeneration: builder.mutation<
      IDraftRunDetail,
      { runId: string; instructions?: string }
    >({
      query: ({ runId, instructions }) => ({
        url: `/contract-model-drafts/runs/${runId}/generate`,
        method: "POST",
        // Always send the field (even empty) so an emptied box clears the stored value server-side.
        body: { instructions: instructions ?? "" },
      }),
      transformResponse: (r: { data: IDraftRunDetail }) => r.data,
      invalidatesTags: ["ContractModelDrafts"],
    }),
    acceptContractModelDraftRecommendation: builder.mutation<
      IDraftRunDetail,
      IAcceptRequest
    >({
      query: ({ recommendationId, ...body }) => ({
        url: `/contract-model-drafts/recommendations/${recommendationId}/accept`,
        method: "POST",
        body,
      }),
      transformResponse: (r: { data: IDraftRunDetail }) => r.data,
      invalidatesTags: ["ContractModelDrafts"],
    }),
    rejectContractModelDraftRecommendation: builder.mutation<
      IDraftRunDetail,
      string
    >({
      query: (recommendationId) => ({
        url: `/contract-model-drafts/recommendations/${recommendationId}/reject`,
        method: "POST",
      }),
      transformResponse: (r: { data: IDraftRunDetail }) => r.data,
      invalidatesTags: ["ContractModelDrafts"],
    }),

    /** Queue an instruction for the next regeneration. Deliberately does NOT start one. */
    addContractModelDraftMessage: builder.mutation<
      IDraftRunDetail,
      {
        runId: string;
        body: string;
        stepId?: string;
        recommendationId?: string;
      }
    >({
      query: ({ runId, ...body }) => ({
        url: `/contract-model-drafts/runs/${runId}/messages`,
        method: "POST",
        body,
      }),
      transformResponse: (r: { data: IDraftRunDetail }) => r.data,
      invalidatesTags: ["ContractModelDrafts"],
    }),

    deleteContractModelDraftMessage: builder.mutation<
      IDraftRunDetail,
      { runId: string; messageId: string }
    >({
      query: ({ runId, messageId }) => ({
        url: `/contract-model-drafts/runs/${runId}/messages/${messageId}`,
        method: "DELETE",
      }),
      transformResponse: (r: { data: IDraftRunDetail }) => r.data,
      invalidatesTags: ["ContractModelDrafts"],
    }),

    /**
     * CLE-2811 — the conversation. Records her message and regenerates now, or queues it behind the pass
     * in flight (which regenerates on its own when it finishes). `queuedOnly` says which happened.
     */
    chatContractModelDraftRun: builder.mutation<
      IChatResult,
      {
        runId: string;
        body: string;
        stepId?: string;
        recommendationId?: string;
      }
    >({
      query: ({ runId, ...body }) => ({
        url: `/contract-model-drafts/runs/${runId}/chat`,
        method: "POST",
        body,
      }),
      transformResponse: (r: {
        data: IDraftRunDetail;
        queuedOnly: boolean;
      }) => ({
        run: r.data,
        queuedOnly: r.queuedOnly,
      }),
      invalidatesTags: ["ContractModelDrafts"],
    }),

    /** Re-derive the whole model into a new version, then diff it against the last one. */
    regenerateContractModelDraftRun: builder.mutation<IDraftRunDetail, string>({
      query: (runId) => ({
        url: `/contract-model-drafts/runs/${runId}/regenerate`,
        method: "POST",
      }),
      transformResponse: (r: { data: IDraftRunDetail }) => r.data,
      invalidatesTags: ["ContractModelDrafts"],
    }),

    /**
     * CLE-2811 — "Continue to rates": a facts-stage run becomes full and the whole model is re-derived
     * into a new version. The accepted contract term keeps its verdict when it comes back unchanged.
     */
    continueContractModelDraftRun: builder.mutation<IDraftRunDetail, string>({
      query: (runId) => ({
        url: `/contract-model-drafts/runs/${runId}/continue`,
        method: "POST",
      }),
      transformResponse: (r: { data: IDraftRunDetail }) => r.data,
      invalidatesTags: ["ContractModelDrafts"],
    }),

    /**
     * Cancel a run that is not being worked on. It stays readable as a record, and no longer counts as
     * the "active" run an idempotent create returns — which is what lets a fresh start happen.
     */
    cancelContractModelDraftRun: builder.mutation<IDraftRunDetail, string>({
      query: (runId) => ({
        url: `/contract-model-drafts/runs/${runId}/cancel`,
        method: "POST",
      }),
      transformResponse: (r: { data: IDraftRunDetail }) => r.data,
      invalidatesTags: ["ContractModelDrafts"],
    }),

    /**
     * CLE-2811 — Stop the pass in flight and KEEP the run (every Stop on the page; no dialog). A revision pass goes
     * back to the model on screen, a first pass that saved nothing back to Generate, one whose facts were saved finishes
     * on them. 409 while the result is being saved, or when nothing is running.
     */
    stopContractModelDraftPass: builder.mutation<IDraftRunDetail, string>({
      query: (runId) => ({
        url: `/contract-model-drafts/runs/${runId}/stop`,
        method: "POST",
      }),
      transformResponse: (r: { data: IDraftRunDetail }) => r.data,
      invalidatesTags: ["ContractModelDrafts"],
    }),

    /**
     * Delete a CANCELED run that produced nothing. The server is the authority (409 naming the failed
     * condition); S3 objects — including files shared with the contract — are never touched.
     */
    deleteContractModelDraftRun: builder.mutation<void, string>({
      query: (runId) => ({
        url: `/contract-model-drafts/runs/${runId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ContractModelDrafts"],
    }),

    /** Undo a reject — what makes deselecting a service line reversible. */
    restoreContractModelDraftRecommendation: builder.mutation<
      IDraftRunDetail,
      string
    >({
      query: (recommendationId) => ({
        url: `/contract-model-drafts/recommendations/${recommendationId}/restore`,
        method: "POST",
      }),
      transformResponse: (r: { data: IDraftRunDetail }) => r.data,
      invalidatesTags: ["ContractModelDrafts"],
    }),
    setContractModelDraftRecommendationImplemented: builder.mutation<
      IDraftRunDetail,
      { runId: string; recommendationId: string; implemented: boolean }
    >({
      query: ({ recommendationId, implemented }) => ({
        url: `/contract-model-drafts/recommendations/${recommendationId}/implemented`,
        method: "POST",
        body: { implemented },
      }),
      transformResponse: (r: { data: IDraftRunDetail }) => r.data,
      // Ticking a checkbox is a one-field change, so flip it in the cache immediately (instant feedback)
      // and reconcile with the server's run when it returns — no full-run refetch, no invalidatesTags.
      onQueryStarted: async (
        { runId, recommendationId, implemented },
        { dispatch, queryFulfilled }
      ) => {
        const patch = dispatch(
          contractModelDraftsQueries.util.updateQueryData(
            "getContractModelDraftRun",
            runId,
            (draft) => {
              const rec = draft.recommendations.find(
                (r) => r.id === recommendationId
              );
              if (rec)
                rec.implementedAt = implemented
                  ? new Date().toISOString()
                  : null;
            }
          )
        );
        try {
          const { data } = await queryFulfilled;
          dispatch(
            contractModelDraftsQueries.util.updateQueryData(
              "getContractModelDraftRun",
              runId,
              (draft) => Object.assign(draft, data)
            )
          );
        } catch {
          patch.undo();
        }
      },
    }),
    runContractModelDraftCompare: builder.mutation<
      IDraftCompareResult,
      { runId: string }
    >({
      query: ({ runId }) => ({
        url: `/contract-model-drafts/runs/${runId}/compare`,
        method: "POST",
        body: {},
      }),
      transformResponse: (r: { data: IDraftCompareResult }) => r.data,
    }),
    getContractModelDraftVerification: builder.query<
      IDraftVerification,
      string
    >({
      query: (runId) => ({
        url: `/contract-model-drafts/runs/${runId}/verification`,
      }),
      transformResponse: (r: { data: IDraftVerification }) => r.data,
    }),
    // "Nothing is modeled for this run's provider+payer, but a sibling provider does have it" — surfaced
    // as a banner so a wrong-provider run doesn't read as an entirely un-modeled contract.
    getContractModelDraftProviderHint: builder.query<IProviderHint, string>({
      query: (runId) => ({
        url: `/contract-model-drafts/runs/${runId}/provider-hint`,
      }),
      transformResponse: (r: { data: IProviderHint }) => r.data,
    }),
    overrideContractModelDraftRecommendation: builder.mutation<
      IDraftRunDetail,
      IOverrideRequest
    >({
      query: ({ recommendationId, ...body }) => ({
        url: `/contract-model-drafts/recommendations/${recommendationId}/override`,
        method: "POST",
        body,
      }),
      transformResponse: (r: { data: IDraftRunDetail }) => r.data,
      invalidatesTags: ["ContractModelDrafts"],
    }),
    // Generates the base-rate-file CSV a base_rate_file recommendation calls for and returns it as a blob
    // so the caller can trigger a browser download. Pass `proposedModeling` to preview UNSAVED edits (POST);
    // omit it to generate from the persisted recommendation (GET).
    getContractModelDraftBaseRateFileCsv: builder.query<
      { blob: Blob; fileName: string },
      { recommendationId: string; proposedModeling?: Record<string, unknown> }
    >({
      query: ({ recommendationId, proposedModeling }) => ({
        url: `/contract-model-drafts/recommendations/${recommendationId}/base-rate-file.csv`,
        ...(proposedModeling
          ? { method: "POST", body: { proposedModeling } }
          : {}),
        responseHandler: async (response: Response) => {
          if (!response.ok) {
            try {
              return await response.json();
            } catch {
              return { message: "Could not generate the base rate file." };
            }
          }
          const cd = response.headers.get("content-disposition") ?? "";
          const match = cd.match(/filename="?([^"]+)"?/);
          return {
            blob: await response.blob(),
            fileName: match ? match[1] : "base-rate-file.csv",
          };
        },
      }),
    }),
    // The base rate files a live contract already prices this run's provider+payer against — surfaced so
    // the reviewer can reuse one instead of generating and uploading a fresh file.
    getContractModelDraftExistingBaseRateFiles: builder.query<
      IExistingBaseRateFiles,
      string
    >({
      query: (runId) => ({
        url: `/contract-model-drafts/runs/${runId}/existing-base-rate-files`,
      }),
      transformResponse: (r: { data: IExistingBaseRateFiles }) => r.data,
    }),
    /** CLE-2811 — every contract type's standing instructions (active version + history). */
    getContractModelingInstructions: builder.query<
      IContractModelingInstructionEntry[],
      void
    >({
      query: () => ({ url: "/contract-model-drafts/instructions" }),
      transformResponse: (r: { data: IContractModelingInstructionEntry[] }) =>
        r.data,
      providesTags: ["ContractModelingInstructions"],
    }),
    /** CLE-2811 — save the next version (append-only; an empty body is stored as "cleared"). */
    saveContractModelingInstruction: builder.mutation<
      IContractModelingInstructionEntry,
      { contractType: IContractType; body: string }
    >({
      query: ({ contractType, body }) => ({
        url: `/contract-model-drafts/instructions/${contractType}`,
        method: "PUT",
        body: { body },
      }),
      transformResponse: (r: { data: IContractModelingInstructionEntry }) =>
        r.data,
      invalidatesTags: ["ContractModelingInstructions"],
    }),
  }),
});

export const {
  useGetContractModelingInstructionsQuery,
  useSaveContractModelingInstructionMutation,
  useListContractModelDraftRunsQuery,
  useGetContractModelDraftRunQuery,
  useGetContractModelDraftRunProgressQuery,
  useGetLatestContractModelRunQuery,
  useCreateContractModelDraftRunMutation,
  useApplyContractModelDraftContractTermMutation,
  useConfirmContractModelDraftDocumentMutation,
  usePresignContractModelDraftDocumentMutation,
  useRunContractModelDraftGenerationMutation,
  useAcceptContractModelDraftRecommendationMutation,
  useRejectContractModelDraftRecommendationMutation,
  useRestoreContractModelDraftRecommendationMutation,
  useAddContractModelDraftMessageMutation,
  useDeleteContractModelDraftMessageMutation,
  useRegenerateContractModelDraftRunMutation,
  useChatContractModelDraftRunMutation,
  useContinueContractModelDraftRunMutation,
  useCancelContractModelDraftRunMutation,
  useStopContractModelDraftPassMutation,
  useDeleteContractModelDraftRunMutation,
  useSetContractModelDraftRecommendationImplementedMutation,
  useRunContractModelDraftCompareMutation,
  useGetContractModelDraftVerificationQuery,
  useLazyGetContractModelDraftVerificationQuery,
  useGetContractModelDraftProviderHintQuery,
  useOverrideContractModelDraftRecommendationMutation,
  useLazyGetContractModelDraftBaseRateFileCsvQuery,
  useGetContractModelDraftExistingBaseRateFilesQuery,
} = contractModelDraftsQueries;
